import type {SiteLocale} from "seo/siteMeta";
import type {ContentPageEntry, ContentRelatedLink} from "./contentPages.ts";

/**
 * The grouped list of every other page in a locale's corpus, for the one page that indexes it
 * (ui#1540).
 *
 * **Why this exists.** Before it, the 27-page Romanian corpus was reachable from exactly ONE link on
 * the rest of the site, and the pages beyond that link were reachable only through each other: 10 of
 * them sat 3 clicks from the home page, 4 sat 5 or 6 clicks away, and Search Console reported 17 of 41
 * sitemap URLs as "Discovered - currently not indexed" or "URL is unknown to Google". A hand-written
 * list of links would have fixed that once and rotted at the next page, because the whole point of the
 * content pipeline is that a new page is ONE markdown file and nothing else. So the index is derived.
 *
 * **The grouping is DATA, not markup** (the `logic-outside-react` rule). Which section a page belongs
 * to is its path's first segment, which the pipeline already validates; what a section is CALLED comes
 * from the i18n catalog, keyed on the section id this module returns. The renderer only walks the
 * result.
 *
 * React-free by construction: `contentArticleRoute` calls it while resolving a lazy route, and the
 * unit tests call it directly.
 */

/**
 * The sections a corpus index is grouped into, in the order they are shown.
 *
 * `more` is the catch-all for a page that sits at the tree root rather than under a section prefix
 * (`/alternativa-wetransfer`). It exists so that no page can be silently dropped from the index: a
 * page under a prefix nothing here maps still lands somewhere visible, rather than disappearing from
 * the one list that makes it reachable.
 */
export const CORPUS_SECTIONS = ["guides", "verticals", "comparisons", "more"] as const;

export type CorpusSection = (typeof CORPUS_SECTIONS)[number];

/**
 * Path prefix → section. The keys are ROUTE SEGMENTS, not copy: they are the localized slugs the
 * corpus is already organised by, and they are what the reader sees in the URL.
 */
const SECTION_BY_SEGMENT: Readonly<Record<string, CorpusSection>> = {
	ghid: "guides",
	pentru: "verticals",
	comparatie: "comparisons",
	guide: "guides",
	for: "verticals",
	compare: "comparisons",
	// The German corpus segments (kitecorp/transferify-ui#2510). `ratgeber` matches the label the
	// German catalogue already gives the section (`content.corpusSections.guides: "Ratgeber"`);
	// umlauts do not survive the SLUG rule, so "für" is `fuer`.
	ratgeber: "guides",
	fuer: "verticals",
	vergleich: "comparisons",
};

/** The section a content path belongs to; `more` when its first segment maps to none. */
export function corpusSectionOf(path: string): CorpusSection {
	const segment = path.replace(/^\//, "").split("/")[0];
	return SECTION_BY_SEGMENT[segment] ?? "more";
}

/** One rendered group: the section id the catalog labels, and the pages in it. */
export interface CorpusIndexSection {
	section: CorpusSection;
	links: ContentRelatedLink[];
}

/**
 * Every page of `locale`'s corpus except `indexPath` itself, grouped by section and ordered.
 *
 * Pages keep the order they arrive in — which is the corpus reader's own filename sort — rather than
 * being re-sorted by title. A title sort would need `localeCompare`, and this list is rendered BOTH by
 * the static pre-render in Node and by the browser that hydrates it; the two run different ICU
 * versions, so a collation difference would show up as a hydration mismatch on the busiest Romanian
 * page. Filename order is the same string comparison everywhere.
 *
 * An empty section is omitted rather than rendered as an empty heading.
 */
export function corpusIndexSections(
	entries: ReadonlyArray<Pick<ContentPageEntry, "locale" | "path" | "title">>,
	locale: SiteLocale,
	indexPath: string,
): CorpusIndexSection[] {
	const grouped = new Map<CorpusSection, ContentRelatedLink[]>();
	for (const entry of entries) {
		if (entry.locale !== locale || entry.path === indexPath) {
			continue;
		}
		const section = corpusSectionOf(entry.path);
		const links = grouped.get(section) ?? [];
		links.push({path: entry.path, title: entry.title});
		grouped.set(section, links);
	}
	return CORPUS_SECTIONS.flatMap((section) => {
		const links = grouped.get(section);
		return links === undefined ? [] : [{section, links}];
	});
}
